package org.example.project.engineletter.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.ref
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexGrow
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingBottom
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.example.project.utils.All
import kotlin.math.floor

private const val LETTER_HEADER_NONE = "NONE"
private const val LETTER_HEADER_LOGO = "LOGO"
private const val LETTER_FOOTER_NONE = "NONE"
private const val LETTER_FOOTER_BUSINESS = "BUSINESS"
private const val LETTER_PAGE_WIDTH_PX = 794
private const val LETTER_PAGE_HEIGHT_PX = 1123
private const val LETTER_TEXT_LINE_HEIGHT_PX = 24
private const val LETTER_PAGE_PADDING_TOP_PX = 50
private const val LETTER_LOGO_BLOCK_HEIGHT_PX = 160
private const val LETTER_PAGE_COUNTER_HEIGHT_PX = 42
private const val LETTER_FOOTER_DIVIDER_AND_SPACING_PX = 29
private const val LETTER_FOOTER_BLOCK_HEIGHT_PX = 132
private const val LETTER_EDITOR_TEXTAREA_ID_PREFIX = "letter-editor-page-"

private typealias LetterBriefSysPreviewPage = All

private fun LetterBriefSysPreviewPage(textLines: List<String>, textCapacity: Int): All =
    All(module = textLines.map { All(label = it) }, lfdNr = textCapacity)

private val LetterBriefSysPreviewPage.textLines: List<String>
    get() = module.map { it.label }

private val LetterBriefSysPreviewPage.textCapacity: Int
    get() = lfdNr

@Composable
fun LetterBriefSysLayout(
    rootElementId: String? = null,
    eigeneDatenZeile: String,
    empfaengerFirmenname: String,
    empfaengerStrasseHausnummer: String,
    empfaengerPlzOrt: String,
    absenderFirmenName: String,
    absenderStrasseHausnummer: String,
    absenderPlzOrt: String,
    absenderDatum: String,
    betreffText: String,
    briefText: String,
    headerMode: String,
    footerMode: String,
    grussformelText: String,
    ortDatumText: String,
    logoSrc: String,
    footerLines: List<String>,
    signaturBildSrc: String,
    signaturNameText: String,
    editable: Boolean,
    onEmpfaengerFirmennameChange: (String) -> Unit,
    onEmpfaengerStrasseHausnummerChange: (String) -> Unit,
    onEmpfaengerPlzOrtChange: (String) -> Unit,
    onAbsenderFirmenNameChange: (String) -> Unit,
    onAbsenderStrasseHausnummerChange: (String) -> Unit,
    onAbsenderPlzOrtChange: (String) -> Unit,
    onAbsenderDatumChange: (String) -> Unit,
    onBetreffChange: (String) -> Unit,
    onTextChange: (String) -> Unit,
    onGrussformelChange: (String) -> Unit,
    onSignaturOrtDatumChange: (String) -> Unit,
    onSignaturNameChange: (String) -> Unit,
    onFooterLineChange: (Int, String) -> Unit,
    closingBlockLines: Int = 7,
    closingContent: (@Composable () -> Unit)? = null
) {
    val resolvedFooterLines = List(3) { index -> footerLines.getOrNull(index).orEmpty() }
    val showHeader = headerMode == LETTER_HEADER_LOGO
    val showFooter = footerMode == LETTER_FOOTER_BUSINESS
    val firstPageLines = computeFirstPageTextLines(showHeader = showHeader, showFooter = showFooter)
    val otherPageLines = computeOtherPageTextLines(showHeader = showHeader, showFooter = showFooter)
    val effectiveClosingContent: @Composable () -> Unit = closingContent ?: {
        LetterClosingBlock(
            ortDatumText = ortDatumText,
            grussformelText = grussformelText,
            signaturBildSrc = signaturBildSrc,
            signaturNameText = signaturNameText
        )
    }
    val previewPages = paginateLetterText(
        text = briefText,
        firstPageLines = firstPageLines,
        otherPageLines = otherPageLines,
        closingBlockLines = closingBlockLines
    )

    Div({
        rootElementId?.takeIf { it.isNotBlank() }?.let { attr("id", it) }
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(32.px)
            width(LETTER_PAGE_WIDTH_PX.px)
            property("padding-bottom", "32px")
        }
    }) {
        if (editable) {
            LetterEditorPage(
                eigeneDatenZeile = eigeneDatenZeile,
                empfaengerFirmenname = empfaengerFirmenname,
                empfaengerStrasseHausnummer = empfaengerStrasseHausnummer,
                empfaengerPlzOrt = empfaengerPlzOrt,
                absenderFirmenName = absenderFirmenName,
                absenderStrasseHausnummer = absenderStrasseHausnummer,
                absenderPlzOrt = absenderPlzOrt,
                absenderDatum = absenderDatum,
                betreffText = betreffText,
                briefText = briefText,
                grussformelText = grussformelText,
                ortDatumText = ortDatumText,
                logoSrc = logoSrc,
                footerLines = resolvedFooterLines,
                totalPages = previewPages.size,
                firstPageLines = firstPageLines,
                otherPageLines = otherPageLines,
                showHeader = showHeader,
                showFooter = showFooter,
                onEmpfaengerFirmennameChange = onEmpfaengerFirmennameChange,
                onEmpfaengerStrasseHausnummerChange = onEmpfaengerStrasseHausnummerChange,
                onEmpfaengerPlzOrtChange = onEmpfaengerPlzOrtChange,
                onAbsenderFirmenNameChange = onAbsenderFirmenNameChange,
                onAbsenderStrasseHausnummerChange = onAbsenderStrasseHausnummerChange,
                onAbsenderPlzOrtChange = onAbsenderPlzOrtChange,
                onAbsenderDatumChange = onAbsenderDatumChange,
                onBetreffChange = onBetreffChange,
                onTextChange = onTextChange,
                onGrussformelChange = onGrussformelChange,
                onSignaturOrtDatumChange = onSignaturOrtDatumChange,
                onSignaturNameChange = onSignaturNameChange,
                onFooterLineChange = onFooterLineChange,
                closingBlockLines = closingBlockLines,
                closingContent = effectiveClosingContent
            )
        } else {
            previewPages.forEachIndexed { index, page ->
                LetterPreviewPageView(
                    pageNumber = index + 1,
                    totalPages = previewPages.size,
                    logoSrc = logoSrc,
                    footerLines = resolvedFooterLines,
                    eigeneDatenZeile = eigeneDatenZeile,
                    empfaengerFirmenname = empfaengerFirmenname,
                    empfaengerStrasseHausnummer = empfaengerStrasseHausnummer,
                    empfaengerPlzOrt = empfaengerPlzOrt,
                    absenderFirmenName = absenderFirmenName,
                    absenderStrasseHausnummer = absenderStrasseHausnummer,
                    absenderPlzOrt = absenderPlzOrt,
                    absenderDatum = absenderDatum,
                    betreffText = betreffText,
                    showHeader = showHeader,
                    showFooter = showFooter,
                    textLines = page.textLines,
                    closingContent = effectiveClosingContent.takeIf { index == previewPages.lastIndex }
                )
            }
        }
    }
}

@Composable
private fun LetterEditorPage(
    eigeneDatenZeile: String,
    empfaengerFirmenname: String,
    empfaengerStrasseHausnummer: String,
    empfaengerPlzOrt: String,
    absenderFirmenName: String,
    absenderStrasseHausnummer: String,
    absenderPlzOrt: String,
    absenderDatum: String,
    betreffText: String,
    briefText: String,
    grussformelText: String,
    ortDatumText: String,
    logoSrc: String,
    footerLines: List<String>,
    totalPages: Int,
    firstPageLines: Int,
    otherPageLines: Int,
    showHeader: Boolean,
    showFooter: Boolean,
    onEmpfaengerFirmennameChange: (String) -> Unit,
    onEmpfaengerStrasseHausnummerChange: (String) -> Unit,
    onEmpfaengerPlzOrtChange: (String) -> Unit,
    onAbsenderFirmenNameChange: (String) -> Unit,
    onAbsenderStrasseHausnummerChange: (String) -> Unit,
    onAbsenderPlzOrtChange: (String) -> Unit,
    onAbsenderDatumChange: (String) -> Unit,
    onBetreffChange: (String) -> Unit,
    onTextChange: (String) -> Unit,
    onGrussformelChange: (String) -> Unit,
    onSignaturOrtDatumChange: (String) -> Unit,
    onSignaturNameChange: (String) -> Unit,
    onFooterLineChange: (Int, String) -> Unit,
    closingBlockLines: Int,
    closingContent: (@Composable () -> Unit)?
) {
    val pages = paginateLetterText(
        text = briefText,
        firstPageLines = firstPageLines,
        otherPageLines = otherPageLines,
        closingBlockLines = closingBlockLines
    )

    pages.forEachIndexed { index, page ->
        LetterPageShell(
            pageNumber = index + 1,
            totalPages = pages.size.coerceAtLeast(1),
            logoSrc = logoSrc,
            footerLines = footerLines,
            showHeader = showHeader,
            showFooter = showFooter,
            closingContent = closingContent.takeIf { index == pages.lastIndex }
        ) {
            LetterMetaBlock(
                eigeneDatenZeile = eigeneDatenZeile,
                empfaengerFirmenname = empfaengerFirmenname,
                empfaengerStrasseHausnummer = empfaengerStrasseHausnummer,
                empfaengerPlzOrt = empfaengerPlzOrt,
                absenderFirmenName = absenderFirmenName,
                absenderStrasseHausnummer = absenderStrasseHausnummer,
                absenderPlzOrt = absenderPlzOrt,
                absenderDatum = absenderDatum,
                betreffText = betreffText,
                editable = true,
                onEmpfaengerFirmennameChange = onEmpfaengerFirmennameChange,
                onEmpfaengerStrasseHausnummerChange = onEmpfaengerStrasseHausnummerChange,
                onEmpfaengerPlzOrtChange = onEmpfaengerPlzOrtChange,
                onAbsenderFirmenNameChange = onAbsenderFirmenNameChange,
                onAbsenderStrasseHausnummerChange = onAbsenderStrasseHausnummerChange,
                onAbsenderPlzOrtChange = onAbsenderPlzOrtChange,
                onAbsenderDatumChange = onAbsenderDatumChange,
                onBetreffChange = onBetreffChange
            )
            var editorElement by remember(index) { mutableStateOf<HTMLElement?>(null) }
            val editorHtmls = remember { mutableStateListOf<String>() }
            while (editorHtmls.size < pages.size) {
                editorHtmls.add(plainTextToHtml(pages[editorHtmls.size].textLines.joinToString("\n")))
            }
            while (editorHtmls.size > pages.size) {
                editorHtmls.removeAt(editorHtmls.lastIndex)
            }

            Div({
                ref { element ->
                    editorElement = element
                    val currentHtml = editorHtmls.getOrNull(index).orEmpty()
                    if (element.innerHTML != currentHtml) {
                        element.innerHTML = currentHtml
                    }
                    onDispose {
                        if (editorElement == element) editorElement = null
                    }
                }
                attr("contenteditable", "true")
                attr("id", "$LETTER_EDITOR_TEXTAREA_ID_PREFIX$index")
                style {
                    letterDocumentEditorAreaStyle()
                    val editorLineCount = page.textCapacity.coerceAtLeast(1)
                    height((editorLineCount * LETTER_TEXT_LINE_HEIGHT_PX).px)
                    property("min-height", "${editorLineCount * LETTER_TEXT_LINE_HEIGHT_PX}px")
                    property("max-height", "${editorLineCount * LETTER_TEXT_LINE_HEIGHT_PX}px")
                    property("line-height", "${LETTER_TEXT_LINE_HEIGHT_PX}px")
                    property("overflow", "hidden")
                    property("padding", "0")
                    property("margin", "0")
                    property("outline", "none")
                    property("box-shadow", "none")
                    property("border-radius", "0")
                    property("white-space", "pre-wrap")
                    property("word-break", "break-word")
                    fontSize(16.px)
                }
                onKeyUp {
                    val currentHtml = editorElement?.innerHTML.orEmpty()
                    editorHtmls[index] = currentHtml
                    val updatedPages = editorHtmls.map(::htmlToPlainText).toMutableList()
                    val pageWasFull = splitEditorLines(updatedPages[index]).size > page.textCapacity
                    onTextChange(updatedPages.joinToString("\n"))

                    if (pageWasFull) {
                        window.setTimeout({
                            val nextTextArea = document.getElementById("$LETTER_EDITOR_TEXTAREA_ID_PREFIX${index + 1}") as? HTMLElement
                            nextTextArea?.focus()
                        }, 0)
                    }
                }
                onBlur {
                    val currentHtml = editorElement?.innerHTML.orEmpty()
                    editorHtmls[index] = currentHtml
                    onTextChange(editorHtmls.map(::htmlToPlainText).joinToString("\n"))
                }
            })
        }
    }
}

@Composable
private fun LetterPreviewPageView(
    pageNumber: Int,
    totalPages: Int,
    logoSrc: String,
    footerLines: List<String>,
    eigeneDatenZeile: String,
    empfaengerFirmenname: String,
    empfaengerStrasseHausnummer: String,
    empfaengerPlzOrt: String,
    absenderFirmenName: String,
    absenderStrasseHausnummer: String,
    absenderPlzOrt: String,
    absenderDatum: String,
    betreffText: String,
    showHeader: Boolean,
    showFooter: Boolean,
    textLines: List<String>,
    closingContent: (@Composable () -> Unit)?
) {
    LetterPageShell(
        pageNumber = pageNumber,
        totalPages = totalPages,
        logoSrc = logoSrc,
        footerLines = footerLines,
        showHeader = showHeader,
        showFooter = showFooter,
        closingContent = closingContent
    ) {
        LetterMetaBlock(
            eigeneDatenZeile = eigeneDatenZeile,
            empfaengerFirmenname = empfaengerFirmenname,
            empfaengerStrasseHausnummer = empfaengerStrasseHausnummer,
            empfaengerPlzOrt = empfaengerPlzOrt,
            absenderFirmenName = absenderFirmenName,
            absenderStrasseHausnummer = absenderStrasseHausnummer,
            absenderPlzOrt = absenderPlzOrt,
            absenderDatum = absenderDatum,
            betreffText = betreffText,
            editable = false,
            onEmpfaengerFirmennameChange = {},
            onEmpfaengerStrasseHausnummerChange = {},
            onEmpfaengerPlzOrtChange = {},
            onAbsenderFirmenNameChange = {},
            onAbsenderStrasseHausnummerChange = {},
            onAbsenderPlzOrtChange = {},
            onAbsenderDatumChange = {},
            onBetreffChange = {}
        )
        Div({
            style {
                flexGrow(1)
                property("white-space", "pre-wrap")
                fontSize(16.px)
                lineHeight(20.px)
            }
        }) {
            Text(textLines.joinToString("\n").ifBlank { " " })
        }
    }
}

@Composable
private fun LetterPageShell(
    pageNumber: Int,
    totalPages: Int,
    logoSrc: String,
    footerLines: List<String>,
    showHeader: Boolean,
    showFooter: Boolean,
    closingContent: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    Div({
        attr("data-letter-pdf-page", "true")
        attr("data-letter-page-number", pageNumber.toString())
        style {
            width(LETTER_PAGE_WIDTH_PX.px)
            height(LETTER_PAGE_HEIGHT_PX.px)
            backgroundColor(Color.white)
            color(Color("#111111"))
            borderRadius(10.px)
            border(1.px, LineStyle.Solid, Color("#d7dbe3"))
            property("box-shadow", "0 30px 70px rgba(0,0,0,0.22)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("box-sizing", "border-box")
            property("padding-left", "25mm")
            property("padding-right", "20mm")
            property("padding-top", "50px")
            property("overflow", "hidden")
        }
    }) {
        if (showHeader) {
            LetterLogoBlock(logoSrc)
        }
        Div({
            style {
                flexGrow(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                minHeight(0.px)
            }
        }) {
            content()
        }
        closingContent?.invoke()
        if (showFooter) {
            Div({
                style {
                    height(1.px)
                    width(100.percent)
                    backgroundColor(Color("#111111"))
                    marginTop(10.px)
                }
            })
            Div({
                style {
                    paddingTop(18.px)
                    paddingBottom(42.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                    fontSize(14.px)
                }
            }) {
                footerLines.forEach { footerValue ->
                    Div { Text(footerValue.ifBlank { " " }) }
                }
            }
        }
    }
}

@Composable
private fun LetterLogoBlock(logoSrc: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            marginBottom(64.px)
        }
    }) {
        if (logoSrc.isBlank()) {
            Div({
                style {
                    width(240.px)
                    minHeight(72.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    backgroundColor(Color("#f3f5f8"))
                    color(Color("#6a7482"))
                    property("border", "1px dashed #c7cfda")
                    property("border-radius", "12px")
                    property("font-size", "13px")
                    property("letter-spacing", "0.04em")
                    property("text-transform", "uppercase")
                }
            }) {
                Text("Logo importieren")
            }
        } else {
            Img(src = logoSrc, attrs = {
                style {
                    property("max-width", "240px")
                    property("max-height", "96px")
                    property("width", "auto")
                    property("height", "auto")
                    property("object-fit", "contain")
                }
            })
        }
    }
}

@Composable
private fun LetterMetaBlock(
    eigeneDatenZeile: String,
    empfaengerFirmenname: String,
    empfaengerStrasseHausnummer: String,
    empfaengerPlzOrt: String,
    absenderFirmenName: String,
    absenderStrasseHausnummer: String,
    absenderPlzOrt: String,
    absenderDatum: String,
    betreffText: String,
    editable: Boolean,
    onEmpfaengerFirmennameChange: (String) -> Unit,
    onEmpfaengerStrasseHausnummerChange: (String) -> Unit,
    onEmpfaengerPlzOrtChange: (String) -> Unit,
    onAbsenderFirmenNameChange: (String) -> Unit,
    onAbsenderStrasseHausnummerChange: (String) -> Unit,
    onAbsenderPlzOrtChange: (String) -> Unit,
    onAbsenderDatumChange: (String) -> Unit,
    onBetreffChange: (String) -> Unit
) {
    Div({ style { marginBottom(18.px); fontSize(12.px) } }) {
        Text(eigeneDatenZeile)
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            marginBottom(32.px)
            gap(32.px)
        }
    }) {
        LetterAddressColumn(
            lines = listOf(empfaengerFirmenname, empfaengerStrasseHausnummer, empfaengerPlzOrt),
            editable = editable,
            alignRight = false,
            onLineChange = { index, value ->
                when (index) {
                    0 -> onEmpfaengerFirmennameChange(value)
                    1 -> onEmpfaengerStrasseHausnummerChange(value)
                    else -> onEmpfaengerPlzOrtChange(value)
                }
            }
        )
        LetterAddressColumn(
            lines = listOf(absenderFirmenName, absenderStrasseHausnummer, absenderPlzOrt),
            editable = editable,
            alignRight = true,
            onLineChange = { index, value ->
                when (index) {
                    0 -> onAbsenderFirmenNameChange(value)
                    1 -> onAbsenderStrasseHausnummerChange(value)
                    else -> onAbsenderPlzOrtChange(value)
                }
            }
        )
    }

    Div({ style { marginBottom(34.px); textAlign("right") } }) {
        if (editable) {
            Input(InputType.Text) {
                value(absenderDatum)
                style {
                    letterDocumentInputStyle()
                    textAlign("right")
                    property("font-weight", "700")
                }
                onInput { onAbsenderDatumChange(it.value) }
            }
        } else {
            Div({ style { letterDocumentReadOnlyStyle(); textAlign("right"); property("font-weight", "700") } }) {
                Text(absenderDatum.ifBlank { " " })
            }
        }
    }

    Div({ style { marginBottom(18.px) } }) {
        if (editable) {
            Input(InputType.Text) {
                value(betreffText)
                style {
                    letterDocumentInputStyle()
                    fontSize(18.px)
                    property("font-weight", "700")
                }
                onInput { onBetreffChange(it.value) }
            }
        } else {
            Div({
                style {
                    letterDocumentReadOnlyStyle()
                    fontSize(18.px)
                    property("font-weight", "700")
                }
            }) {
                Text(betreffText.ifBlank { " " })
            }
        }
    }
}

@Composable
private fun LetterAddressColumn(
    lines: List<String>,
    editable: Boolean,
    alignRight: Boolean,
    onLineChange: (Int, String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            flexGrow(1)
            flexShrink(1)
            property("flex-basis", "0")
            if (alignRight) textAlign("right")
        }
    }) {
        lines.forEachIndexed { index, value ->
            if (editable) {
                Input(InputType.Text) {
                    value(value)
                    style {
                        letterDocumentInputStyle()
                        property("font-weight", "700")
                        if (alignRight) textAlign("right")
                    }
                    onInput { onLineChange(index, it.value) }
                }
            } else {
                Div({
                    style {
                        letterDocumentReadOnlyStyle()
                        property("font-weight", "700")
                        if (alignRight) textAlign("right")
                    }
                }) {
                    Text(value.ifBlank { " " })
                }
            }
        }
    }
}

@Composable
private fun LetterClosingBlock(
    ortDatumText: String,
    grussformelText: String,
    signaturBildSrc: String,
    signaturNameText: String
) {
    Div({
        style {
            marginTop(18.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            flexShrink(0)
        }
    }) {
        Div({ style { letterDocumentReadOnlyStyle() } }) {
            Text(ortDatumText.ifBlank { " " })
        }
        Div({ style { letterDocumentReadOnlyStyle() } }) {
            Text(grussformelText.ifBlank { " " })
        }
        if (signaturBildSrc.startsWith("data:image/")) {
            Img(src = signaturBildSrc, attrs = {
                style {
                    property("max-width", "220px")
                    property("max-height", "72px")
                    property("object-fit", "contain")
                }
            })
        } else {
            Div({ style { minHeight(44.px) } })
        }
        Div({ style { letterDocumentReadOnlyStyle() } }) {
            Text(signaturNameText.ifBlank { " " })
        }
    }
}

@Composable
private fun LetterFooterInputs(
    footerLines: List<String>,
    onFooterLineChange: (Int, String) -> Unit
) {
    Div({
        style {
            marginTop(14.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(6.px)
            paddingBottom(42.px)
        }
    }) {
        footerLines.forEachIndexed { index, footerValue ->
            Input(InputType.Text) {
                value(footerValue)
                style {
                    letterDocumentInputStyle()
                    height(22.px)
                    fontSize(14.px)
                    lineHeight(20.px)
                    padding(0.px)
                }
                onInput { onFooterLineChange(index, it.value) }
            }
        }
    }
}

private fun paginateLetterText(
    text: String,
    firstPageLines: Int,
    otherPageLines: Int,
    closingBlockLines: Int
): List<LetterBriefSysPreviewPage> {
    val lines = splitEditorLines(text)
    val pages = mutableListOf<LetterBriefSysPreviewPage>()
    val remaining = lines.toMutableList()

    fun takeLines(capacity: Int): List<String> {
        val amount = capacity.coerceAtLeast(0).coerceAtMost(remaining.size)
        return List(amount) { remaining.removeAt(0) }
    }

    var firstPage = true
    if (remaining.isEmpty()) {
        pages += LetterBriefSysPreviewPage(
            textLines = emptyList(),
            textCapacity = (firstPageLines - closingBlockLines).coerceAtLeast(1)
        )
        firstPage = false
    }

    while (remaining.isNotEmpty()) {
        val baseCapacity = if (firstPage) firstPageLines else otherPageLines
        val closingCapacity = (baseCapacity - closingBlockLines).coerceAtLeast(1)
        val pageCapacity = when {
            closingBlockLines <= 0 -> baseCapacity
            remaining.size <= closingCapacity -> closingCapacity
            remaining.size <= baseCapacity -> closingCapacity
            else -> baseCapacity
        }
        val pageLines = takeLines(pageCapacity)
        pages += LetterBriefSysPreviewPage(
            textLines = pageLines,
            textCapacity = pageCapacity
        )
        firstPage = false
    }

    return pages
}

private fun computeFirstPageTextLines(showHeader: Boolean, showFooter: Boolean): Int =
    computeTextLines(
        usableHeightPx = LETTER_PAGE_HEIGHT_PX -
            LETTER_PAGE_PADDING_TOP_PX -
            (if (showHeader) LETTER_LOGO_BLOCK_HEIGHT_PX else 0) -
            LETTER_PAGE_COUNTER_HEIGHT_PX -
            (if (showFooter) LETTER_FOOTER_DIVIDER_AND_SPACING_PX + LETTER_FOOTER_BLOCK_HEIGHT_PX else 0)
    )

private fun computeOtherPageTextLines(showHeader: Boolean, showFooter: Boolean): Int =
    computeTextLines(
        usableHeightPx = LETTER_PAGE_HEIGHT_PX -
            LETTER_PAGE_PADDING_TOP_PX -
            (if (showHeader) LETTER_LOGO_BLOCK_HEIGHT_PX else 0) -
            LETTER_PAGE_COUNTER_HEIGHT_PX -
            (if (showFooter) LETTER_FOOTER_DIVIDER_AND_SPACING_PX + LETTER_FOOTER_BLOCK_HEIGHT_PX else 0)
    )

private fun computeTextLines(usableHeightPx: Int): Int =
    floor(usableHeightPx.toDouble() / LETTER_TEXT_LINE_HEIGHT_PX.toDouble()).toInt().coerceAtLeast(1)

private fun splitEditorLines(text: String): List<String> {
    val normalized = text.replace("\r\n", "\n")
    if (normalized.isEmpty()) return emptyList()

    val lines = mutableListOf<String>()
    var startIndex = 0
    normalized.forEachIndexed { index, char ->
        if (char == '\n') {
            lines += normalized.substring(startIndex, index)
            startIndex = index + 1
        }
    }
    lines += normalized.substring(startIndex)
    return lines
}

private fun plainTextToHtml(text: String): String =
    text
        .replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace("\n", "<br>")

private fun htmlToPlainText(html: String): String {
    val host = document.createElement("div") as HTMLElement
    host.innerHTML = html
    return host.textContent.orEmpty().replace("\r\n", "\n")
}

fun buildLetterBriefSysOwnDataLine(
    firmenName: String,
    firmenAdresse: String
): String {
    val addressLines = firmenAdresse
        .replace("\r\n", "\n")
        .split("\n")
        .map { it }
        .filter { it.isNotBlank() }

    val streetHouse = addressLines.getOrNull(0).orEmpty()
    val cityLine = addressLines.getOrNull(1).orEmpty()
    val cityParts = cityLine.split(Regex("\\s+"), limit = 2)
    val plz = cityParts.getOrNull(0).orEmpty()
    val ort = cityParts.getOrNull(1).orEmpty().uppercase()

    return listOf(
        firmenName,
        streetHouse,
        plz,
        ort
    ).joinToString(" | ")
}



private fun StyleScope.letterDocumentInputStyle() {
    width(100.percent)
    backgroundColor(Color.transparent)
    color(Color("#111111"))
    border(0.px, LineStyle.None, Color.transparent)
    padding(0.px)
    fontSize(16.px)
    lineHeight(20.px)
    property("box-sizing", "border-box")
    property("outline", "none")
    property("box-shadow", "none")
}

private fun StyleScope.letterDocumentReadOnlyStyle() {
    width(100.percent)
    color(Color("#111111"))
    fontSize(16.px)
    lineHeight(20.px)
    property("white-space", "pre-wrap")
}

private fun StyleScope.letterDocumentEditorAreaStyle() {
    width(100.percent)
    backgroundColor(Color.transparent)
    color(Color("#111111"))
    border(0.px, LineStyle.None, Color.transparent)
    fontSize(16.px)
    lineHeight(20.px)
    property("box-sizing", "border-box")
    property("outline", "none")
    property("box-shadow", "none")
}


